import React, { memo, useEffect, useRef, useState } from "react";
import {
    GoogleMap,
    withGoogleMap,
    OverlayView,
    Marker,
} from "react-google-maps";
import InfoBox from "react-google-maps/lib/components/addons/InfoBox";
import styled from "styled-components";

import { IMapMarker } from "../../../../Models/IMapMarker";
import { IPoint } from "../../../../Models/IPoint";
import { digitalShareElementText, generationStatus, isOutputSelected, registerElement, registerRef, setPendingRender } from "../../../Common/DigitalShare/DigitalShareGenerator";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { delay, digitalShareMapRender } from "../../../../Util/ScoutDigitalShareUtil";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { RenderType } from "../../../../Enums/RenderType";
import { useSelector } from "react-redux";
import { gpsReportMapSelector } from "../../../../Reducers/Selectors/GPSInsightsSelectors";

interface IFrequentlyVisitedPlacesMapProps {
    markers: IMapMarker[];
    polygons: IPoint[][];
    onMapReady?: () => void;
    renderType?: RenderType;
}

const StyledInfoBox = styled(InfoBox)``;

const FrequentlyVisitedPlacesMap = (props: IFrequentlyVisitedPlacesMapProps): JSX.Element => {

    /** misc */
    const googleMapOptions: google.maps.MapOptions = {
        disableDefaultUI: true,
        gestureHandling: "greedy",
    };

    /** state */
    const mapRef: React.MutableRefObject<GoogleMap> = useRef<GoogleMap>();
    const [polyCenters, setPolyCenters] = useState<google.maps.LatLng[]>([]);
    const [shareTemplate, setShareTemplate] = useState<React.ReactNode>(null);
    const [ref, setRef] = useState<React.MutableRefObject<HTMLDivElement>>(null);

    const {
        buxtonDeck,
    } = useSelector(gpsReportMapSelector);

    /** effects */
    useEffect(() => {
        /** adds the centroid of the polygon as a google point. */
        const polygonMapCenterPoints: google.maps.LatLng[] = [];
        for (const poly of props.polygons) {
            const bounds = new google.maps.LatLngBounds();
            for (const point of poly) {
                const gp = new google.maps.LatLng(point.y, point.x);
                bounds.extend(gp);
            }
            polygonMapCenterPoints.push(bounds.getCenter());
        }
        setPolyCenters(polygonMapCenterPoints);
    }, [props.polygons]);

    useEffect(() => {
        const timeoutPanTo = null;
        const timeoutFitBounds = null;
        if (mapRef.current) {
            /** if a marker is selected, pan to that one */
            const selectedMapMarker = props.markers.find((m: IMapMarker) => {
                return m.isSelected;
            });
            if (selectedMapMarker) {
                /* NOTE
                    Something between the redux store and the google markers is causing an odd effect that moves the map focus.
                    The only way I could stop that was to call fitMapBoundsToMarkers and reset the camera.
                    Unable to fix the original issue, this is more of an "elegant" workaround.
                */
                // reset map zoom
                fitMapBoundsToMarkers(mapRef.current);
                const bounds = new google.maps.LatLngBounds();
                bounds.extend({
                    lat: selectedMapMarker.lat,
                    lng: selectedMapMarker.lng,
                });
                // small delay, then pan to selected marker
                setTimeout(() => {
                    mapRef.current.panTo({
                        lat: selectedMapMarker.lat,
                        lng: selectedMapMarker.lng,
                    });
                }, 500);
                // small delay, snap closer in. Setting a zoom prop is not working in this situation.
                setTimeout(() => {
                    mapRef.current.fitBounds(bounds);
                }, 1000);
            } else {
                /** handles the zoom / center of the map to fit all markers */
                fitMapBoundsToMarkers(mapRef.current);
            }
        }
        // clean up
        return () => {
            clearTimeout(timeoutPanTo);
            clearTimeout(timeoutFitBounds);
        };
    }, [props.markers]);

    const getBoundsFittingAllItems = (): google.maps.LatLngBounds => {
        const bounds = new google.maps.LatLngBounds();
        /** extend bounds to fit all markers */
        for (const marker of props.markers) {
            bounds.extend(marker);
        }
        /** extend bounds to fit all polygon centroids */
        for (const poly of props.polygons) {
            for (const point of poly) {
                const gp = new google.maps.LatLng(point.y, point.x);
                bounds.extend(gp);
            }
        }
        return bounds;
    };

    const fitMapBoundsToMarkers = (map: GoogleMap): void => {
        const bounds: google.maps.LatLngBounds = getBoundsFittingAllItems();
        map.fitBounds(bounds);
    };

    useEffect(() => {
        let isStopped = false;
        let timeout = null;
        if (
            props.renderType === RenderType.DIGITAL_SHARE
            && isOutputSelected(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.FrequentlyVisitedPlacesMap
            )
        ) {
            // Tell the system we're starting to render.
            setPendingRender(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.FrequentlyVisitedPlacesMap,
                true
            );

            // Using a set timeout here to wait for the redraw() to take effect.
            timeout = setTimeout(() => {
                const run = async () => {
                    const ref = React.createRef<HTMLDivElement>();

                    // // Create an image from the map
                    const el = document.querySelector("#fvp-map") as HTMLElement;
                    el.style.width = "1250px";
                    el.style.height = "700px";

                    digitalShareElementText.set(DigitalShareItemType.FrequentlyVisitedPlacesMap, "");

                    // Waiting here so that images can show up on the progress screen.
                    await delay(1000);

                    if (
                        // If the component has been cleaned up...
                        isStopped
                        // Or if there has been an error and the process has stopped...
                        || generationStatus.get(DigitalShareAppType.Mobilytics) === false
                    ) {
                        // Then tell the system we're not exporting this component anymore
                        // and don't do any more processing.
                        setPendingRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.FrequentlyVisitedPlacesMap,
                            false
                        );
                    }
                    // Otherwise, if exporting should proceed...
                    else {
                        // Get the template element to plunk in the DOM so it can be accessed by the generator.
                        const template = await digitalShareMapRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.FrequentlyVisitedPlacesMap,
                            el,
                            ref
                        );

                        setShareTemplate(template);
                        setRef(ref);
                        // Tell the system we're not rendering anymore (for cancellation cleanup).
                        setPendingRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.FrequentlyVisitedPlacesMap,
                            false
                        );
                    }
                };
                run();
            }, 100);
        }

        return () => {
            if (props.renderType === RenderType.DEFAULT) {
                isStopped = true;
            }

            clearTimeout(timeout);
        };

    }, [props.renderType]);

    useEffect(() => {
        if (ref && shareTemplate) {
            registerRef(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.FrequentlyVisitedPlacesMap,
                ref
            );
        }
    }, [ref, shareTemplate]);

    return (
        <div id="fvp-map">
            <div
                id="deck-tooltip"
                style={{
                    position: "absolute",
                    zIndex: 1,
                    pointerEvents: "none",
                    background: "none",
                }}
            ></div>
            <GoogleMap
                options={googleMapOptions}
                ref={mapRef}
                onTilesLoaded={props.onMapReady}
            >
                {polyCenters.map((p: google.maps.LatLng, index: number) => {
                    return (
                        <Marker
                            key={`${index}-fvp-polygon-center-marker`}
                            position={{
                                lat: p.lat(),
                                lng: p.lng(),
                            }}
                        />
                    );
                })}

                {props.markers.map((m: IMapMarker, i: number) => {
                    return (
                        <OverlayView
                            /** https://github.com/tomchentw/react-google-maps/issues/482 */
                            /** GB - NOTE:
                             * I cannot get this to stop throwing a markerLayer error without
                             * the Math.random() here. I've tried all kinds of different
                             * combinations that would create a unique id for the key,
                             * but so far Math.random() is the only thing that has worked for me.
                             */
                            key={`${i}-fvp-marker-${m.id}-${Math.random()}`}
                            position={{
                                lat: m.lat,
                                lng: m.lng,
                            }}
                            mapPaneName={OverlayView.OVERLAY_LAYER}
                        >
                            <div>
                                <StyledInfoBox
                                    defaultPosition={
                                        new google.maps.LatLng(m.lat, m.lng)
                                    }
                                    options={{
                                        pixelOffset: new google.maps.Size(-8, -40),
                                        closeBoxURL: "",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            backgroundColor: "white",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "5px",
                                        }}
                                    >
                                        {/* this accounts for the info window close button being gone */}
                                        <div
                                            style={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                display: "flex",
                                                justifyContent: "center",
                                                backgroundColor: "rgb(28,225,128)",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: "20px",
                                                    color: "white",
                                                }}
                                            >{m.rank}</span>
                                        </div>
                                    </div>
                                </StyledInfoBox>

                                <div
                                    style={{
                                        position: "absolute",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: `${m.width}px`,
                                        height: `${m.height}px`,
                                        background: "#6B1A9B",
                                        color: "#fff",
                                        borderRadius: "50%",
                                        border: "1px solid #c4c4c4",
                                    }}
                                >
                                    {m.icon ? (
                                        <img
                                            src={m.icon}
                                            style={{
                                                maxWidth: "100%",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    ) : (
                                        <span>{m.name}</span>
                                    )}
                                </div>
                            </div>
                        </OverlayView>
                    );
                })}
            </GoogleMap>
        </div>
    );
};

const FrequentlyVisitedPlacesMapWithGoogleMap = withGoogleMap(FrequentlyVisitedPlacesMap);
const FrequentlyVisitedPlacesMapWithGoogleMapMemo = memo(FrequentlyVisitedPlacesMapWithGoogleMap, (prevProps, nextProps) => {
    const markersEqual = prevProps.markers === nextProps.markers;
    const polygonsEqual = prevProps.polygons === nextProps.polygons;
    const allEqual = markersEqual && polygonsEqual;

    return allEqual;
});
export default FrequentlyVisitedPlacesMapWithGoogleMapMemo;